import { Component } from '@angular/core';
import {
    airspaceAuthorisationsRequiringDataManagement,
    airspaceAuthorisationsRequiringDataManagementDisplayLabels
} from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CurrentPersonDto,
    ExclusiveControlService,
    LoggedInUser,
    SimpleAirspaceJurisdictionDto,
    UserService,
    UserStatus,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { NotificationSettingsService } from './notification-settings/notification-settings.service';

@Component({
    selector: 'profile-dialogue',
    templateUrl: './profile-dialogue.component.html',
    providers: [ExclusiveControlService, NotificationSettingsService]
})
export class ProfileDialogueComponent {
    currentUser$: Observable<CurrentPersonDto | undefined>;

    private ngUnsubscribe$ = new Subject<void>();

    private workTracker = new WorkTracker();

    working = false;

    locked = false;

    editingHistoricalFlights = false;

    hasApiKeyOrganisations = false;

    hasNotamOrganisations = false;

    hasAirspaceData = false;
    jurisdictionsWithAirspaceDataManagement: SimpleAirspaceJurisdictionDto[] =
        [];
    airspaceAuthorisationsRequiringDataManagementDisplayLabels =
        airspaceAuthorisationsRequiringDataManagementDisplayLabels;

    constructor(
        private modal: BsModalRef<ProfileDialogueComponent>,
        modalOptions: ModalOptions,
        private userService: UserService,
        private exclusiveControlService: ExclusiveControlService,
        private commonDialoguesService: CommonDialoguesService
    ) {
        this.currentUser$ = this.userService.userChange$.pipe(
            filter(c => c.type === UserStatus.LOGGED_IN),
            map((change: LoggedInUser) => change.currentUser),
            startWith(this.userService.getCurrentUser())
        );

        modalOptions.closeInterceptor = () => {
            if (this.locked === true) {
                return this.commonDialoguesService
                    .showConfirmationDialogue(
                        'Confirm Cancel',
                        `You have unsaved changes, are you sure you want to cancel?`,
                        'Yes',
                        () => Promise.resolve()
                    )
                    .then(() => {
                        this.exclusiveControlService.lock(() => true);
                        this.exclusiveControlService.requestUnlock();
                    });
            } else {
                return Promise.resolve();
            }
        };
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.refreshFeatureFlags();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshFeatureFlags() {
        const userOrgs = this.userService.findUsersOrganisations();
        this.hasApiKeyOrganisations =
            userOrgs.filter(org => org.featureFlags.includes('apiKeys')) !=
                null &&
            userOrgs.filter(org => org.featureFlags.includes('apiKeys'))
                .length > 0;
        this.hasNotamOrganisations =
            userOrgs.filter(org => org.featureFlags.includes('notams')) !=
                null &&
            userOrgs.filter(org => org.featureFlags.includes('notams')).length >
                0;
        this.hasAirspaceData = userOrgs.some(org =>
            org.activeJurisdictions.some(jurisdiction =>
                airspaceAuthorisationsRequiringDataManagement.includes(
                    jurisdiction.identifier
                )
            )
        );
        if (this.hasAirspaceData) {
            this.jurisdictionsWithAirspaceDataManagement = userOrgs
                .filter(org =>
                    org.activeJurisdictions.some(jurisdiction =>
                        airspaceAuthorisationsRequiringDataManagement.includes(
                            jurisdiction.identifier
                        )
                    )
                )
                .reduce(
                    (acc, org) =>
                        acc.concat(
                            ...org.activeJurisdictions.filter(
                                jurisdiction =>
                                    airspaceAuthorisationsRequiringDataManagement.includes(
                                        jurisdiction.identifier
                                    ) &&
                                    acc.find(j => j.id === jurisdiction.id) ==
                                        null
                            )
                        ),
                    []
                );
        }
    }

    close() {
        this.modal.hide();
    }

    onEditHistoricalFlights() {
        this.editingHistoricalFlights = true;
    }

    onDoneEditingHistoricalFlights() {
        this.editingHistoricalFlights = false;
    }
}
